import type { ReactNode } from 'react';
import InlineStatus from './InlineStatus';
import SetupTrustHelperBand from './SetupTrustHelperBand';
import type { PortfolioCoverageSummary } from '../../lib/portfolioCoverageSummary';

const proofToneClass = {
  info: 'trust-proof--info',
  success: 'trust-proof--success',
  warning: 'trust-proof--warning',
  error: 'trust-proof--error',
} as const;

interface PortfolioCoverageSummaryRailProps {
  summary: PortfolioCoverageSummary;
  action?: ReactNode;
  className?: string;
  titleOverride?: ReactNode;
  testId?: string;
}

export default function PortfolioCoverageSummaryRail({
  summary,
  action,
  className = '',
  titleOverride,
  testId,
}: PortfolioCoverageSummaryRailProps) {
  if (summary.isSkeleton) {
    return (
      <div
        className={`rounded-xl border border-border bg-surface p-5 ${className}`.trim()}
        data-testid={testId}
      >
        <div className="space-y-3">
          <div className="skeleton h-4 w-40" />
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="rounded-lg border border-border bg-surface-active p-3">
                <div className="skeleton h-3 w-20" />
                <div className="skeleton mt-2 h-4 w-full" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      <InlineStatus
        tone={summary.tone}
        title={titleOverride ?? summary.title}
        message={(
          <div className="space-y-3">
            {summary.surface === 'monitoring' && summary.facts ? (
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
                {summary.facts.map((fact) => (
                  <div key={fact.key} className="rounded-lg border border-border bg-surface-active/70 p-3">
                    <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-gray-500">
                      {fact.label}
                    </p>
                    <p
                      className={[
                        'mt-2 text-sm text-gray-100',
                        fact.mono ? 'font-mono tabular-nums' : '',
                        fact.tone === 'success'
                          ? 'text-emerald-300'
                          : fact.tone === 'warning'
                            ? 'text-amber-300'
                            : fact.tone === 'error'
                              ? 'text-red-300'
                              : '',
                      ].filter(Boolean).join(' ')}
                    >
                      {fact.value}
                    </p>
                  </div>
                ))}
              </div>
            ) : null}
            <p className="text-sm text-gray-300">{summary.message}</p>
            {summary.surface !== 'monitoring' && summary.facts ? (
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
                {summary.facts.map((fact) => (
                  <div key={fact.key} className="rounded-lg border border-border bg-surface-active/70 p-3">
                    <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-gray-500">
                      {fact.label}
                    </p>
                    <p
                      className={[
                        'mt-2 text-sm text-gray-100',
                        fact.mono ? 'font-mono tabular-nums' : '',
                        fact.tone === 'success'
                          ? 'text-emerald-300'
                          : fact.tone === 'warning'
                            ? 'text-amber-300'
                            : fact.tone === 'error'
                              ? 'text-red-300'
                              : '',
                      ].filter(Boolean).join(' ')}
                    >
                      {fact.value}
                    </p>
                  </div>
                ))}
              </div>
            ) : !summary.facts ? (
              <div className="space-y-1.5">
                {summary.lines?.map((line) => (
                  <p key={line.key} className={`trust-proof ${proofToneClass[line.tone]}`}>
                    <span className="trust-proof__label">{line.headline}</span>
                    <span className="trust-proof__separator">·</span>
                    <span className="trust-proof__meta">{line.meta}</span>
                  </p>
                ))}
              </div>
            ) : null}
            {summary.helper ? (
              <SetupTrustHelperBand
                tone={summary.helper.tone}
                eyebrow={summary.helper.eyebrow}
                message={summary.helper.message}
              />
            ) : null}
          </div>
        )}
        action={action}
        className={`rounded-xl ${className}`.trim()}
      />
    </div>
  );
}
